import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Menu, X, ExternalLink, Shield, Factory, Headphones } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { getPartnerLogoUrl } from '../utils/pocketbaseHelpers';
import { loggingService } from '../services/loggingService';
import { notificationService } from '../services/notificationService';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { NotificationModal } from './NotificationModal';

export const Header: React.FC = () => {
  const { user, logout, partnerCampaigns, marketingCampaigns, changePassword, updateProfile, partners, createContactInquiry } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCompanyProfile, setShowCompanyProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordMessageType, setPasswordMessageType] = useState<'success' | 'error'>('success');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const navigate = useNavigate();
  const [editableName, setEditableName] = useState('');
  const [showChangeRequestForm, setShowChangeRequestForm] = useState(false);
  const [changeRequestText, setChangeRequestText] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info'
  });
  const partnerName = user?.partner_id
    ? partners.find(partner => partner.id === user.partner_id)?.name
    : null;
  const partnerDetails = user?.partner_id ? partners.find(partner => partner.id === user.partner_id) : null;

  const getRoleBadge = (role: string) => {
    const styles = {
      global_admin: 'bg-red-100 text-red-800',
      partner_admin: 'bg-blue-100 text-blue-800',
      user: 'bg-green-100 text-green-800',
    };
    
    const labels = {
      global_admin: 'Global Admin',
      partner_admin: 'Partner Admin',
      user: 'User',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role as keyof typeof styles]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage('Please fill in all password fields.');
      setPasswordMessageType('error');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage('New password and confirmation do not match.');
      setPasswordMessageType('error');
      return;
    }

    if (newPassword.length < 10) {
      setPasswordMessage('New password must be at least 10 characters long.');
      setPasswordMessageType('error');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      setPasswordMessage(null);
      await changePassword(currentPassword, newPassword);
      setPasswordMessage('Password updated successfully.');
      setPasswordMessageType('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordMessage(error?.message || 'Failed to update password.');
      setPasswordMessageType('error');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const getMagicLink = () => {
    if (user?.role === 'partner_admin' && user?.partner_id) {
      // Find the active partner campaign for this partner
      const activePartnerCampaign = partnerCampaigns.find(pc => 
        pc.partner_id === user.partner_id && pc.status === 'active'
      );
      
      if (activePartnerCampaign) {
        // Check if this partner campaign is included in any approved marketing campaign
        const isIncludedInApprovedCampaign = marketingCampaigns.some(mc => 
          mc.status === 'active' && mc.partner_campaign_ids.includes(activePartnerCampaign.id)
        );
        
        if (isIncludedInApprovedCampaign) {
          const link = `${window.location.origin}/public-campaigns?partner=${user.partner_id}&partnerCampaign=${activePartnerCampaign.id}`;
          navigator.clipboard.writeText(link);
          setNotification({
            isOpen: true,
            title: 'Magic Link Copied',
            message: 'Your magic link is ready to share and has been copied to the clipboard.',
            type: 'success'
          });
        } else {
          setNotification({
            isOpen: true,
            title: 'Approval Required',
            message: 'Your campaign must be approved in a global campaign before you can generate a magic link.',
            type: 'warning'
          });
        }
      } else {
        setNotification({
          isOpen: true,
          title: 'No Active Campaign',
          message: 'You need at least one active campaign to generate a magic link.',
          type: 'warning'
        });
      }
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
              <span className="text-white font-bold text-lg">ML</span>
            </div>
            <div className="ml-3">
              <h1 className="text-xl font-semibold text-gray-800">Magic Links ✨</h1>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {/* Notification Bell */}
            {user && (
              <NotificationBell userId={user.id} userName={user.name} />
            )}
            {partnerDetails && (
              <button
                type="button"
                onClick={() => setShowCompanyProfile(true)}
                className="p-2 rounded-full text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition"
                title="View company profile"
              >
                <Factory size={20} />
              </button>
            )}
            
            <button
              type="button"
              onClick={() => setShowProfile(true)}
              className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg px-2 py-1"
            >
              <User size={20} className="text-gray-500" />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-800">{user?.name}</span>
                {getRoleBadge(user?.role || 'user')}
              </div>
            </button>
            
            {/* Help Desk Support */}
            <a
              href="mailto:support@clickandclaim.eu"
              className="p-2 rounded-full text-gray-500 hover:text-green-600 hover:bg-green-50 transition"
              title="Contact Support"
            >
              <Headphones size={20} />
            </a>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition duration-200"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-600 hover:text-gray-800"
            >
              {showMenu ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {showMenu && (
          <div className="md:hidden pb-4 border-t border-gray-200 mt-4 pt-4">
            <div className="flex flex-col space-y-3">
              {/* Notification Bell for Mobile */}
              {user && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Notifications</span>
                  <NotificationBell userId={user.id} userName={user.name} />
                </div>
              )}
              
              {user?.role === 'partner_admin' && (
                <button
                  onClick={getMagicLink}
                  className="flex items-center space-x-2 text-green-600 hover:text-green-800 transition duration-200"
                >
                  <ExternalLink size={20} />
                  <span>Get my magic link</span>
                </button>
              )}
              {partnerDetails && (
                <button
                  type="button"
                  onClick={() => setShowCompanyProfile(true)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition duration-200"
                >
                  <Factory size={20} />
                  <span>Company Profile</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowProfile(true)}
                className="flex items-center space-x-3 text-left focus:outline-none"
              >
                <User size={20} className="text-gray-500" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{user?.name}</div>
                  {getRoleBadge(user?.role || 'user')}
                </div>
              </button>
              
              {/* Help Desk Support - Mobile */}
              <a
                href="mailto:support@clickandclaim.eu"
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition duration-200"
              >
                <Headphones size={20} />
                <span>Contact Support</span>
              </a>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition duration-200"
              >
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}

        {showProfile && user && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Account Settings</h3>
                  <p className="text-sm text-gray-500">View your account details and update your password</p>
                </div>
                <button
                  onClick={() => {
                    setShowProfile(false);
                    setPasswordMessage(null);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-6 space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Full Name</label>
                    <div className="mt-1 flex gap-3">
                      <input
                        type="text"
                        value={editableName || user.name}
                        onChange={(e) => setEditableName(e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter full name"
                      />
                      <button
                        type="button"
                        disabled={isUpdatingProfile || !editableName?.trim() || editableName === user.name}
                        onClick={async () => {
                          if (!editableName.trim() || editableName === user.name) return;
                          try {
                            setIsUpdatingProfile(true);
                            await updateProfile({ name: editableName.trim() });
                            setPasswordMessage('Full name updated successfully.');
                            setPasswordMessageType('success');
                          } catch (error: any) {
                            setPasswordMessage(error?.message || 'Failed to update name.');
                            setPasswordMessageType('error');
                          } finally {
                            setIsUpdatingProfile(false);
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="mt-1 text-gray-900">{user.email}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Role</label>
                      <div className="mt-1 inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full uppercase tracking-wide">
                        {user.role?.replace('_', ' ')}
                      </div>
                    </div>
                    {user.partner_id && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Partner Name</label>
                        <p className="mt-1 text-gray-900 font-medium">
                          {partnerName || '—'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Created</label>
                      <p className="mt-1 text-gray-900">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Last Login</label>
                      <p className="mt-1 text-gray-900">
                        {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <Shield size={18} className="text-blue-600" />
                    <h4 className="text-md font-semibold text-gray-900">Change Password</h4>
                  </div>
                  <form className="space-y-4" onSubmit={handlePasswordChange}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter current password"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="At least 8 characters"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Re-enter new password"
                          required
                        />
                      </div>
                    </div>
                    {passwordMessage && (
                      <div className={`rounded-lg px-4 py-3 text-sm ${
                        passwordMessageType === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {passwordMessage}
                      </div>
                    )}
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowProfile(false);
                          setPasswordMessage(null);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdatingPassword}
                        className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isUpdatingPassword && <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        Update Password
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCompanyProfile && partnerDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Company Profile</h3>
                  <p className="text-sm text-gray-500">Overview for {partnerDetails.name}</p>
                </div>
                <button
                  onClick={() => setShowCompanyProfile(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(() => {
                    const logoUrl = getPartnerLogoUrl(partnerDetails);
                    return logoUrl ? (
                      <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-sm text-gray-500 mb-2">Company Logo</p>
                        <img
                          src={logoUrl}
                          alt={`${partnerDetails.name} logo`}
                          className="max-h-20 object-contain"
                        />
                      </div>
                    ) : null;
                  })()}
                  <div>
                    <p className="text-sm text-gray-500">Company Name</p>
                    <p className="mt-1 text-gray-900 font-semibold text-lg">{partnerDetails.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mt-1 ${partnerDetails.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {partnerDetails.status || 'unknown'}
                    </span>
                  </div>

                  {partnerDetails.category && (
                    <div>
                      <p className="text-sm text-gray-500">Category</p>
                      <p className="mt-1 text-gray-900">{partnerDetails.category}</p>
                    </div>
                  )}
                  {partnerDetails.web_domain && (
                    <div>
                      <p className="text-sm text-gray-500">Website</p>
                      <a
                        href={partnerDetails.web_domain.startsWith('http') ? partnerDetails.web_domain : `https://${partnerDetails.web_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        {partnerDetails.web_domain}
                      </a>
                    </div>
                  )}

                  {partnerDetails.privacy_policy_url && (
                    <div>
                      <p className="text-sm text-gray-500">Privacy Policy</p>
                      <a
                        href={partnerDetails.privacy_policy_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        View privacy policy
                      </a>
                    </div>
                  )}

                  {partnerDetails.region && partnerDetails.region.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">Regions</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {partnerDetails.region.map((region) => (
                          <span key={region} className="px-2 py-1 text-xs font-semibold bg-blue-50 text-blue-700 rounded-full">
                            {region}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Change Request Section */}
                {!showChangeRequestForm ? (
                  <div className="border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowChangeRequestForm(true)}
                      className="w-full px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                    >
                      Request Information Change
                    </button>
                  </div>
                ) : (
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Describe the changes you need
                      </label>
                      <textarea
                        value={changeRequestText}
                        onChange={(e) => setChangeRequestText(e.target.value)}
                        placeholder="Please describe what information needs to be updated (e.g., company name, logo, website, privacy policy URL, etc.)"
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    {requestMessage && (
                      <div className={`p-3 rounded-lg text-sm ${requestMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        {requestMessage.text}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!changeRequestText.trim()) {
                            setRequestMessage({ text: 'Please describe the changes needed', type: 'error' });
                            return;
                          }
                          
                          setIsSubmittingRequest(true);
                          setRequestMessage(null);
                          
                          try {
                            // Create contact inquiry using AuthContext (updates state and triggers proper flow)
                            const inquiryData = {
                              name: user?.name || '',
                              email: user?.email || '',
                              subject: `Company Profile Change Request - ${partnerDetails.name}`,
                              message: `Partner: ${partnerDetails.name}\nPartner ID: ${partnerDetails.id}\n\nRequested Changes:\n${changeRequestText}`
                            };
                            console.log('📝 Creating contact inquiry:', inquiryData);
                            await createContactInquiry(inquiryData);
                            console.log('✅ Contact inquiry created successfully');
                            
                            // Log the request
                            if (user) {
                              await loggingService.logCompanyProfileChangeRequest(
                                user.id,
                                user.name,
                                user.role as 'global_admin' | 'partner_admin',
                                partnerDetails.id,
                                partnerDetails.name,
                                changeRequestText
                              );
                            }
                            
                            // Notify all global admins
                            const { data: globalAdmins } = await DataService.getGlobalAdmins();
                            console.log('📢 Found global admins:', globalAdmins?.length || 0);
                            if (globalAdmins && globalAdmins.length > 0) {
                              for (const admin of globalAdmins) {
                                console.log(`📧 Sending notification to admin: ${admin.email}`);
                                const result = await notificationService.createNotification({
                                  user_id: admin.id,
                                  type: 'system_announcement',
                                  title: 'Company Profile Change Request',
                                  message: `${partnerDetails.name} requested profile changes. Check Contact Inquiries for details.`,
                                  link: '/inquiries',
                                  metadata: { partner_id: partnerDetails.id, partner_name: partnerDetails.name },
                                  priority: 'high',
                                  notification_scope: 'global_admins'
                                });
                                console.log('✅ Notification result:', result);
                              }
                            } else {
                              console.warn('⚠️ No global admins found to notify');
                            }
                            
                            setRequestMessage({ text: 'Change request submitted successfully!', type: 'success' });
                            setChangeRequestText('');
                            setTimeout(() => {
                              setShowChangeRequestForm(false);
                              setRequestMessage(null);
                            }, 2000);
                          } catch (error: any) {
                            console.error('Failed to submit change request:', error);
                            setRequestMessage({ text: error.message || 'Failed to submit request', type: 'error' });
                          } finally {
                            setIsSubmittingRequest(false);
                          }
                        }}
                        disabled={isSubmittingRequest || !changeRequestText.trim()}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmittingRequest ? 'Submitting...' : 'Submit Request'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowChangeRequestForm(false);
                          setChangeRequestText('');
                          setRequestMessage(null);
                        }}
                        disabled={isSubmittingRequest}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowCompanyProfile(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <NotificationModal
          isOpen={notification.isOpen}
          onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
          title={notification.title}
          message={notification.message}
          type={notification.type}
          autoClose={notification.type === 'success'}
          autoCloseDelay={2500}
        />
      </div>
    </header>
  );
};